/**
 * ProductFormV2Omega - Enhanced product form for Omega template
 * Handles products with Omega Add On as primary option
 */
class ProductFormV2Omega {
	constructor() {
		this.form = document.querySelector(".product-form-v2")
		if (!this.form) return
		
		this.productData = null
		this.currentSelections = null
		this.elements = null
		this.lastMonthSupplyByType = { onetime: null, subscribe: null }
		this.pendingSelectionRestore = null
		
		this.productData = this.buildProductData()
		this.currentSelections = this.getInitialSelections()
		this.elements = this.getElements()
		this.init()
	}

	buildProductData() {
		if (!this.form) return { variants: [], subscriptionPlans: [] }

		if (window.productFormV2?.variants) {
			const variants = window.productFormV2.variants
			const subscriptionPlans = this.buildSubscriptionPlans()
			return { variants, subscriptionPlans }
		}

		const variants = []
		this.form.querySelectorAll(".variant-option").forEach((option) => {
			const variantId = option.dataset.variantId
			const omegaAddOn = option.dataset.variantOmega || option.dataset.variantAddon
			const monthSupply = option.dataset.monthSupply
			const price = parseFloat(option.dataset.variantPrice) || 0
			const comparePrice = parseFloat(option.dataset.variantComparePrice) || 0
			const available = option.dataset.available !== "false"

			variants.push({
				id: variantId,
				option1: omegaAddOn, // Omega Add On
				option2: monthSupply, // Quantity
				price: price * 100, // Convert to cents for consistency
				compare_at_price: comparePrice * 100, // Convert to cents
				available
			})
		})

		const subscriptionPlans = this.buildSubscriptionPlans()
		return { variants, subscriptionPlans }
	}

	buildSubscriptionPlans() {
		const subscriptionPlans = []

		this.form.querySelectorAll(".plan-option[data-plan-id]").forEach((option) => {
			const planId = option.dataset.planId
			const planName = option.dataset.planName
			const months = parseInt(option.dataset.months) || 1
			const price = parseFloat(option.dataset.price) || 0
			const total = parseFloat(option.dataset.total) || 0
			const originalPrice = parseFloat(option.dataset.originalPrice) || 0
			const discount = parseInt(option.dataset.discount) || 0

			subscriptionPlans.push({
				id: planId,
				name: planName,
				months,
				price,
				total,
				originalPrice,
				discount
			})
		})

		return subscriptionPlans
	}

	getInitialSelections() {
		if (!this.form || !this.productData) {
			return {
				omegaAddOn: null,
				variant: null,
				subscriptionPlan: null,
				purchaseType: "onetime",
				monthSupply: null
			}
		}

		// Get first available variant, preferring 3 Month Supply
		const availableVariants = this.productData.variants.filter(v => v.available)
		const selectedVariant =
			availableVariants.find((v) => v.option2 === '3 Month Supply') ||
			availableVariants[0] ||
			this.productData.variants[0]
		// Use the selling plan that belongs to the selected variant (each supply length has its own plan).
		// Defaulting to the first plan sent the 3 Month variant with the 30 day plan, which Shopify rejects (422).
		const selectedPlanInput =
			this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked') ||
			this.form.querySelector(`.subscription-plans input[name="selling_plan_ui"][data-variant-id="${selectedVariant?.id}"]`)
		const selectedPlanId = selectedPlanInput?.closest(".plan-option")?.dataset.planId
		const selectedPlan =
			this.productData.subscriptionPlans.find((p) => p.id?.toString() === selectedPlanId) ||
			this.productData.subscriptionPlans[0]

		return {
			omegaAddOn: selectedVariant?.option1,
			variant: selectedVariant,
			subscriptionPlan: selectedPlan,
			purchaseType: selectedPlan ? "subscribe" : "onetime",
			monthSupply: selectedVariant?.option2
		}
	}

	getElements() {
		const elements = {
			purchaseTypeTabs: this.form.querySelectorAll(".tab-button"),
			subscriptionPlans: this.form.querySelector(".subscription-plans"),
			onetimeOptions: this.form.querySelector(".onetime-options"),
			planInputs: this.form.querySelectorAll('.plan-option input[type="radio"]'),
			variantInputs: this.form.querySelectorAll('input[name="variant_id"]'),
			totalPricing: this.form.querySelector(".total-pricing"),
			originalTotal: this.form.querySelector(".original-total"),
			finalTotal: this.form.querySelector(".final-total"),
			hiddenVariantId: this.form.querySelector('input[name="id"][type="hidden"]'),
			hiddenSellingPlan: this.form.querySelector('input[name="selling_plan"][type="hidden"]'),
		}
		return elements
	}

	init() {
		// Remove load class to show options
		const loadElements = document.querySelectorAll('.product-form-v2-option.load')
		loadElements.forEach((option) => {
			option.classList.remove('load')
		})
		
		this.ensureHiddenFieldsExist()
		this.setupEventListeners()
		// Sync tabs and protection messaging with the initial purchase type (Subscribe & Save by default)
		this.updatePurchaseType(this.currentSelections.purchaseType)
		this.updateDisplay()
		this.updatePricing()
		this.updateFormFields()
		this.updateAddToCartButton()
		
		// Setup add to cart handler
		this.addToCartHandler = (e) => {
			e.preventDefault()
			this.handleAddToCart()
		}
		const addToCartButton = this.form.querySelector(".add-to-cart-button")
		if (addToCartButton) {
			addToCartButton.addEventListener('click', this.addToCartHandler)
		}
	}

	setupEventListeners() {
		if (!this.elements) return

		// Purchase type tabs
		this.elements.purchaseTypeTabs.forEach((tab) => {
			tab.addEventListener("click", (e) => {
				e.preventDefault()
				const purchaseType = tab.dataset.purchaseType
				this.updatePurchaseType(purchaseType)
			})
		})

		// Subscription plan selection
		this.elements.planInputs.forEach((input) => {
			input.addEventListener("change", () => {
				const planIdWithIndex = input.value
				const resolvedPlanId = planIdWithIndex.toString().split("_")[0]
				const monthSupply = input.getAttribute("option2")
				
				this.currentSelections.monthSupply = monthSupply
				this.lastMonthSupplyByType.subscribe = monthSupply
				
				if (this.elements.hiddenSellingPlan) {
					this.elements.hiddenSellingPlan.value = resolvedPlanId
				}
				
				const planVariantId = input.dataset.variantId
				if (planVariantId) {
					this.updateVariant(planVariantId)
				}
				this.updateSubscriptionPlan(resolvedPlanId)
				
				// Ensure selected class is properly applied
				this.form.querySelectorAll('.plan-option').forEach(option => {
					option.classList.remove('selected')
				})
				const planOption = input.closest('.plan-option')
				if (planOption) {
					planOption.classList.add('selected')
				}
				
				this.updateDisplay()
				this.updatePricing() // Explicitly call pricing update
				this.updateFormFields()
				this.updateAddToCartButton()
			})
		})

		// Variant selection for one-time
		this.elements.variantInputs.forEach((input) => {
			input.addEventListener("change", (e) => {
				const variantId = e.target.value
				const variantOption = input.closest('.variant-option')
				const monthSupply = variantOption?.dataset?.monthSupply
				
				this.currentSelections.monthSupply = monthSupply
				this.lastMonthSupplyByType.onetime = monthSupply
				
				this.updateVariant(variantId)
				this.updateDisplay()
				this.updatePricing()
				this.updateFormFields()
				this.updateAddToCartButton()
			})
		})

		// Handle direct clicks on option elements
		this.setupOptionClickHandlers()
	}

	setupOptionClickHandlers() {
		// Handle clicks on variant options
		this.form.querySelectorAll('.variant-option').forEach((option) => {
			option.addEventListener('click', (e) => {
				if (e.target.type === 'radio') return
				
				const radio = option.querySelector('input[type="radio"]')
				if (radio && !radio.disabled) {
					radio.checked = true
					radio.dispatchEvent(new Event('change', { bubbles: true }))
				}
			})
		})

		// Handle clicks on plan options
		this.form.querySelectorAll('.plan-option').forEach((option) => {
			option.addEventListener('click', (e) => {
				if (e.target.type === 'radio') return
				
				const radio = option.querySelector('input[type="radio"]')
				if (radio && !radio.disabled) {
					radio.checked = true
					radio.dispatchEvent(new Event('change', { bubbles: true }))
				}
			})
		})
	}

	updatePurchaseType(purchaseType) {
		if (!this.currentSelections) return

		this.currentSelections.purchaseType = purchaseType

		// Update tab visibility
		this.elements.purchaseTypeTabs.forEach((tab) => {
			tab.classList.toggle("active", tab.dataset.purchaseType === purchaseType)
		})

		// Update protection sections
		const onetimeProtection = this.form.querySelector('.out_of_stock_protection.onetime-views')
		const subscribeProtection = this.form.querySelector('.protection-section.subscribe-views2')
		const outOfStockProtectionText = this.form.querySelector('[data-out-of-stock-protection-text]')
		const inStockProtectionText = this.form.querySelector('[data-in-stock-protection-text]')

		if (purchaseType === 'onetime') {
			onetimeProtection?.classList.remove('hidden')
			subscribeProtection?.classList.add('hidden')
			outOfStockProtectionText?.classList.remove('hidden')
			inStockProtectionText?.classList.add('hidden')
		} else {
			onetimeProtection?.classList.add('hidden')
			subscribeProtection?.classList.remove('hidden')
			outOfStockProtectionText?.classList.add('hidden')
			inStockProtectionText?.classList.remove('hidden')
		}

		this.updateDisplay()
		this.updatePricing()
		this.updateFormFields()
		this.updateAddToCartButton()
	}

	updateSubscriptionPlan(planId) {
		const normalizedId = planId?.toString().split("_")[0]
		const plan = this.productData.subscriptionPlans.find((p) => p.id?.toString() === normalizedId)
		if (plan) {
			this.currentSelections.subscriptionPlan = plan
		}
	}

	updateVariant(variantId) {
		// First check window.productFormV2 variants
		if (window.productFormV2?.variants) {
			const variant = window.productFormV2.variants.find((v) => v.id?.toString() === variantId?.toString())
			if (variant) {
				this.currentSelections.variant = variant
				this.currentSelections.omegaAddOn = variant.option1
				this.currentSelections.monthSupply = variant.option2
				// Immediately update pricing after variant change
				this.updatePricing()
				return
			}
		}
		
		// Fallback to built product data
		const variant = this.productData.variants.find((v) => v.id?.toString() === variantId?.toString())
		if (variant) {
			this.currentSelections.variant = variant
			this.currentSelections.omegaAddOn = variant.option1
			this.currentSelections.monthSupply = variant.option2
			// Immediately update pricing after variant change
			this.updatePricing()
		}
	}

	updateDisplay() {
		if (!this.currentSelections || !this.elements) return

		const { purchaseType } = this.currentSelections

		// Show/hide subscription vs one-time options
		if (purchaseType === "subscribe") {
			this.elements.subscriptionPlans?.classList.remove("hidden")
			this.elements.onetimeOptions?.classList.add("hidden")

			// Auto-select first available plan if none selected
			const checkedPlan = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
			if (!checkedPlan) {
				const firstAvailable = this.form.querySelector('.subscription-plans .plan-option:not(.hidden) input[type="radio"]')
				if (firstAvailable) {
					firstAvailable.checked = true
					firstAvailable.dispatchEvent(new Event('change', { bubbles: true }))
				}
			}
		} else {
			this.elements.subscriptionPlans?.classList.add("hidden")
			this.elements.onetimeOptions?.classList.remove("hidden")

			// Auto-select first available variant if none selected
			const checkedVariant = this.form.querySelector('.onetime-options input[name="variant_id"]:checked')
			if (!checkedVariant) {
				const firstAvailable = this.form.querySelector('.onetime-options .variant-option:not(.hidden) input[type="radio"]')
				if (firstAvailable) {
					firstAvailable.checked = true
					firstAvailable.dispatchEvent(new Event('change', { bubbles: true }))
				}
			}
		}

		this.updateVariantVisualSelection()
		
		// Always update pricing after display update
		setTimeout(() => {
			this.updatePricing()
		}, 100)
	}

	updateVariantVisualSelection() {
		// Remove selected class from all options
		this.form.querySelectorAll(".variant-option, .plan-option").forEach((option) => {
			option.classList.remove("selected")
		})

		// Add selected class to the checked option
		if (this.currentSelections.purchaseType === "subscribe") {
			const checkedPlan = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
			if (checkedPlan) {
				const planOption = checkedPlan.closest('.plan-option')
				planOption?.classList.add("selected")
			}
		} else {
			const checkedVariant = this.form.querySelector('.onetime-options input[name="variant_id"]:checked')
			if (checkedVariant) {
				const variantOption = checkedVariant.closest('.variant-option')
				variantOption?.classList.add("selected")
			}
		}
	}

	updatePricing() {
		if (!this.currentSelections?.variant) return

		const variant = this.currentSelections.variant
		
		// Get base prices from variant data
		let finalPrice = 0
		let originalPrice = 0
		
		// If we have variant data from window.productFormV2
		if (window.productFormV2?.variants) {
			const foundVariant = window.productFormV2.variants.find(v => v.id?.toString() === variant.id?.toString())
			if (foundVariant) {
				originalPrice = foundVariant.compare_at_price ? foundVariant.compare_at_price / 100 : foundVariant.price / 100
				finalPrice = foundVariant.price / 100
				
				// For subscriptions, apply the selling plan price
				if (this.currentSelections.purchaseType === "subscribe") {
					const checkedPlan = this.form.querySelector('.subscription-plans input[name="selling_plan_ui"]:checked')
					if (checkedPlan) {
						const variantId = checkedPlan.dataset.variantId
						const planId = checkedPlan.value.split('_')[0]
						
						// Find the selling plan allocation for this variant
						if (foundVariant.selling_plan_allocations) {
							const allocation = foundVariant.selling_plan_allocations.find(a => 
								a.selling_plan_id?.toString() === planId?.toString()
							)
							
							if (allocation && allocation.price) {
								// Use the selling plan's discounted price
								finalPrice = allocation.price / 100
							} else if (allocation && allocation.price_adjustments && allocation.price_adjustments.length > 0) {
								const adjustment = allocation.price_adjustments[0]
								if (adjustment.value_type === 'percentage') {
									// Apply percentage discount
									finalPrice = originalPrice * (1 - adjustment.value / 100)
								} else if (adjustment.value_type === 'fixed_amount') {
									// Apply fixed amount discount
									finalPrice = originalPrice - (adjustment.value / 100)
								} else {
									// Value is the new price
									finalPrice = adjustment.value / 100
								}
							}
						}
					}
				}
			}
		} else {
			// Fallback to variant object prices
			finalPrice = (variant.price || 0) / 100
			originalPrice = (variant.compare_at_price || variant.price || 0) / 100
		}

		// Ensure finalPrice is never 0 or negative
		if (finalPrice <= 0) {
			finalPrice = originalPrice
		}

		// Update display
		if (this.elements?.finalTotal) {
			this.elements.finalTotal.textContent = this.formatMoney(finalPrice)
		}

		if (this.elements?.originalTotal) {
			// Show original price if it exists and is different from final price
			if (originalPrice > 0 && Math.abs(originalPrice - finalPrice) > 0.01) {
				this.elements.originalTotal.textContent = this.formatMoney(originalPrice)
				this.elements.originalTotal.style.display = 'inline-block'
				this.elements.originalTotal.style.textDecoration = 'line-through'
				this.elements.originalTotal.style.opacity = '0.6'
				this.elements.originalTotal.style.marginRight = '8px'
			} else {
				this.elements.originalTotal.textContent = ''
				this.elements.originalTotal.style.display = 'none'
			}
		}

		// Ensure the total pricing container is visible
		if (this.elements?.totalPricing) {
			this.elements.totalPricing.style.display = 'flex'
			this.elements.totalPricing.style.alignItems = 'center'
			this.elements.totalPricing.style.justifyContent = 'flex-end'
		}
	}

	updateFormFields() {
		if (!this.elements || !this.currentSelections) return

		const { variant, subscriptionPlan, purchaseType } = this.currentSelections

		if (this.elements.hiddenVariantId && variant?.id) {
			this.elements.hiddenVariantId.value = variant.id
		}

		if (this.elements.hiddenSellingPlan) {
			this.elements.hiddenSellingPlan.value = purchaseType === "subscribe" && subscriptionPlan?.id ? subscriptionPlan.id : ""
		}
	}

	updateAddToCartButton() {
		const addToCartButton = this.form.querySelector('.add-to-cart-button')
		if (!addToCartButton) return
		
		const text = addToCartButton.querySelector('.add-to-cart-text')
		const selectedOption = this.form.querySelector(`.product-form-v2-option.selected:not(.hidden)`)
		const isAvailable = selectedOption?.dataset.available !== "false"

		if (isAvailable && this.currentSelections?.variant?.available) {
			addToCartButton.disabled = false
			addToCartButton.style.opacity = "1"
			if (text) text.innerHTML = "ADD TO CART"
		} else {
			addToCartButton.disabled = true
			addToCartButton.style.opacity = "0.5"
			if (text) text.innerHTML = "Out of Stock"
		}
	}

	ensureHiddenFieldsExist() {
		if (!this.elements.hiddenVariantId) {
			const hiddenVariantId = document.createElement('input')
			hiddenVariantId.type = 'hidden'
			hiddenVariantId.name = 'id'
			this.form.appendChild(hiddenVariantId)
			this.elements.hiddenVariantId = hiddenVariantId
		}

		if (!this.elements.hiddenSellingPlan) {
			const hiddenSellingPlan = document.createElement('input')
			hiddenSellingPlan.type = 'hidden'
			hiddenSellingPlan.name = 'selling_plan'
			this.form.appendChild(hiddenSellingPlan)
			this.elements.hiddenSellingPlan = hiddenSellingPlan
		}
	}

	handleAddToCart() {
		if (!this.validateForm()) return

		const addToCartButton = this.form.querySelector('.add-to-cart-button')
		const buttonText = addToCartButton?.querySelector('.add-to-cart-text')
		
		if (addToCartButton) {
			addToCartButton.disabled = true
			addToCartButton.style.opacity = "0.5"
		}
		if (buttonText) {
			buttonText.innerHTML = "Adding to Cart..."
		}

		const formData = new FormData(this.form)

		fetch('/cart/add.js', {
			method: 'POST',
			body: formData
		})
		.then(response => {
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
			return response.json()
		})
		.then(data => {
			console.log("✅ Successfully added to cart:", data)
			
			// Trigger cart update events
			const cartUpdateEvent = new CustomEvent('cart:updated', {
				detail: { cart: data },
				bubbles: true
			})
			document.dispatchEvent(cartUpdateEvent)
			
			// Redirect to cart if needed
			const currentUrl = new URL(window.location.href)
			if (currentUrl.searchParams.get('view_cart') === null) {
				currentUrl.searchParams.append('view_cart', true)
				// Uncomment to redirect: window.location.href = currentUrl.toString()
			}
		})
		.catch(error => {
			console.error("❌ Error adding to cart:", error)
		})
		.finally(() => {
			if (addToCartButton) {
				addToCartButton.disabled = false
				addToCartButton.style.opacity = "1"
			}
			if (buttonText) {
				buttonText.innerHTML = "ADD TO CART"
			}
		})
	}

	validateForm() {
		if (!this.currentSelections?.variant?.id) {
			console.error("No variant selected")
			return false
		}

		if (this.currentSelections.purchaseType === "subscribe" && !this.currentSelections?.subscriptionPlan?.id) {
			console.error("No subscription plan selected")
			return false
		}

		return true
	}

	formatMoney(amount) {
		if (window.Shopify?.formatMoney) {
			return window.Shopify.formatMoney(amount * 100)
		}
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	calculateStockPercentage() {
		const startDateElement = document.querySelector('#stock-period-start')
		const endDateElement = document.querySelector('#stock-period-end')
		
		if (!startDateElement || !endDateElement) return

		const startDate = new Date(startDateElement.dataset.date)
		const endDate = new Date(endDateElement.dataset.date)
		const today = new Date()

		const totalDuration = endDate.getTime() - startDate.getTime()
		const elapsedTime = today.getTime() - startDate.getTime()
		
		let percentage = Math.round((elapsedTime / totalDuration) * 100)
		percentage = Math.max(0, Math.min(100, percentage))
		
		const remainingPercent = 100 - percentage

		const remainingBlock = document.querySelector('.remaining-block')
		const soldThroughElement = document.querySelector('#sold-through-percent')

		if (remainingBlock) {
			remainingBlock.style.flexBasis = `${remainingPercent}%`
		}

		if (soldThroughElement) {
			soldThroughElement.textContent = `${percentage}% Sold Through`
		}
	}
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
	const productFormV2Omega = new ProductFormV2Omega()
	productFormV2Omega.calculateStockPercentage()
})